import { lowestPlanPrice, highestPlanPrice } from "@/data/pricing";
import { LastUpdated } from "@/components/site/LastUpdated";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout, Section, CTAButton, BOOKING_URL } from "@/components/site/Layout";
import { PricingTeaser } from "@/components/site/Pricing";
import { CaseStudy, serviceCaseStudy } from "@/components/site/CaseStudy";
import { TrustSignals } from "@/components/site/TrustSignals";
import { FAQSection } from "@/components/site/FAQSection";
import { HeroLeadForm } from "@/components/site/HeroLeadForm";
import CoreWebVitalsChecker from "@/components/blog/CoreWebVitalsChecker";
import { Check } from "lucide-react";
import { services } from "@/data/services";
import { jsonLdScript, ogImageFor } from "@/lib/seo";

const TITLE = "Technical SEO for Law Firms & Lawyers | PageOne Lawyers";
const DESCRIPTION =
  "Technical SEO for law firms: crawl, indexing, speed and schema fixes, with your audit and priority fixes in 15 to 20 days. Included in every SEO plan.";
const URL = "https://pageonelawyers.com/services/technical-seo-for-law-firms";
const OG_IMAGE = ogImageFor("service", "technical-seo-for-law-firms");
const SCHEMAS = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://pageonelawyers.com/services/technical-seo-for-law-firms#webpage",
    url: "https://pageonelawyers.com/services/technical-seo-for-law-firms",
    name: "Technical SEO for Law Firms & Lawyers | PageOne Lawyers",
    description:
      "Technical SEO for law firms: crawl, indexing, speed and schema fixes, with your audit and priority fixes in 15 to 20 days. Included in every SEO plan.",
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      name: "PageOne Lawyers",
      url: "https://pageonelawyers.com/",
    },
    breadcrumb: {
      "@id": "https://pageonelawyers.com/services/technical-seo-for-law-firms#breadcrumb",
    },
    mainEntity: {
      "@id": "https://pageonelawyers.com/services/technical-seo-for-law-firms#service",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://pageonelawyers.com/services/technical-seo-for-law-firms#service",
    name: "Technical SEO for Law Firms",
    serviceType: "Technical SEO",
    description:
      "Technical SEO services for law firms and attorneys: crawlability and indexation audits, site speed and Core Web Vitals fixes, mobile SEO, schema markup, site architecture, HTTPS and redirect clean-up, and monthly technical monitoring.",
    url: "https://pageonelawyers.com/services/technical-seo-for-law-firms",
    provider: {
      "@type": "Organization",
      name: "PageOne Lawyers",
      url: "https://pageonelawyers.com/",
    },
    audience: {
      "@type": "BusinessAudience",
      audienceType: "Law firms and attorneys",
    },
    areaServed: {
      "@type": "Country",
      name: "United States",
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: lowestPlanPrice,
      highPrice: highestPlanPrice,
      offerCount: "6",
      url: "https://pageonelawyers.com/pricing",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": "https://pageonelawyers.com/services/technical-seo-for-law-firms#breadcrumb",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://pageonelawyers.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: "https://pageonelawyers.com/services",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Technical SEO for Law Firms",
        item: "https://pageonelawyers.com/services/technical-seo-for-law-firms",
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://pageonelawyers.com/services/technical-seo-for-law-firms#faq",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is technical SEO for law firms?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "It's the work that makes sure search engines can crawl, index and understand your site: speed, mobile usability, site structure, redirects, duplicate pages and schema markup. It doesn't replace content and links, but it decides whether they can work.",
        },
      },
      {
        "@type": "Question",
        name: "How long does a technical SEO audit take?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Most firms have the full audit and priority fixes within 15 to 20 days. After that, we monitor the site every month and fix new issues as they appear.",
        },
      },
      {
        "@type": "Question",
        name: "Do you make the fixes or just send a report?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Either, depending on your firm. We can make the changes on your site ourselves, give your developer a clear fix list, or split the work between us.",
        },
      },
      {
        "@type": "Question",
        name: "Is technical SEO included in your SEO plans?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Technical audits, fixes and monthly monitoring are included in every SEO plan. Website migrations are a separate project, quoted on their own.",
        },
      },
      {
        "@type": "Question",
        name: "Do Core Web Vitals affect rankings?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "They're part of how Google assesses page experience, but relevance still matters more. We treat them as one part of technical SEO, not the whole job.",
        },
      },
      {
        "@type": "Question",
        name: "What schema markup should a law firm use?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Usually LegalService for the firm and each office, Person for each attorney, Service for practice pages and BreadcrumbList for navigation. Avoid the deprecated Attorney type, and only mark up what's visible on the page.",
        },
      },
      {
        "@type": "Question",
        name: "Can you help if we're moving to a new website?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Website migration SEO is a separate project from the monthly plan: we map every redirect, protect your existing rankings and check everything after launch.",
        },
      },
      {
        "@type": "Question",
        name: "Which website platforms do you work with?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "All the common CMS platforms law firms use, including WordPress. We confirm the access we need during the audit.",
        },
      },
      {
        "@type": "Question",
        name: "Which tools do you use?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Google Search Console, PageSpeed Insights, the Rich Results Test, Screaming Frog, Ahrefs and Semrush, plus manual checks of your most important pages.",
        },
      },
    ],
  },
] as const;
const FAQS = SCHEMAS[3].mainEntity.map((f) => ({ q: f.name, a: f.acceptedAnswer.text }));
export const Route = createFileRoute("/services/technical-seo-for-law-firms")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:type", content: "image/svg+xml" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Technical SEO for Law Firms — PageOne Lawyers" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: SCHEMAS.map(jsonLdScript),
  }),
  component: TechnicalSeoPage,
});
function TechnicalSeoPage() {
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-primary-foreground/65">
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link to="/">Home</Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link to="/services">Services</Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>Technical SEO for Law Firms</li>
            </ol>
          </nav>
          <h1 className="max-w-4xl font-display text-4xl font-semibold leading-tight sm:text-5xl md:text-6xl">
            {"Technical SEO Services for Law Firms"}
          </h1><LastUpdated />
          <p className="mt-6 max-w-3xl text-xl text-primary-foreground/85">
            {
              "Fix what stops Google from crawling, indexing and ranking your firm's pages: slow load times, crawl errors, mobile problems and broken schema."
            }
          </p>
          <p className="mt-5 max-w-4xl text-lg leading-relaxed text-primary-foreground/75">
            {
              "Technical SEO for law firms makes sure Google can find, read and trust every page on your site. You can have the strongest practice pages in your market, but if they're slow on a phone, blocked from crawling or missing from Google's index, they won't bring in cases. We audit your site, fix the issues in order of impact and keep watching so new problems don't creep back. Most firms have their full audit and priority fixes within 15 to 20 days."
            }
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <CTAButton to="#free-plan" variant="gold">
              Get a free technical audit
            </CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">
              Talk to a strategist
            </CTAButton>
          </div>
        </div>
      </section>
      <div className="border-y border-border bg-secondary/40">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"What's Included in Our Technical SEO for Law Firms"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Law firm technical SEO covers everything under the surface of your website. This is what we check and fix."
            }
          </p>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">
                {"Website crawlability audit for law firms"}
              </h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "We crawl your whole site the way Google does and find what blocks it: robots.txt rules, broken links, redirect chains, orphaned practice pages and links hidden in scripts Google can't follow."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Indexation audit for law firm websites"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "We compare the pages you want ranking with the pages Google has actually indexed, then fix the reasons for the gap: stray noindex tags, wrong canonicals, duplicate location pages and thin tag or archive pages."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Site speed optimization for law firms"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "Oversized images, heavy chat and intake widgets, stacked plugins and slow hosting are the usual causes of slow law firm sites. We find which ones are slowing your pages and fix them."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Core Web Vitals fixes"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "We measure each page template against Google's Core Web Vitals using real visitor data where it exists, then fix template by template, so one change improves every page built on it."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Mobile SEO for law firms"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "Google indexes the mobile version of your site. We check that mobile pages carry the same content, links and schema as desktop, that phone numbers and buttons are easy to tap, and that nothing covers the content."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Schema markup for law firms"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "LegalService, Person for each attorney, Service and BreadcrumbList markup, written to match what's visible on each page and tested in Google's Rich Results Test."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Site architecture and internal links"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "A clear path from your homepage to practice areas, case types and locations, so authority reaches the pages that bring in cases and nothing important is buried."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"HTTPS, redirects and duplicate URLs"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "One secure, canonical version of every page: no mixed content, no http or www duplicates, no redirect loops."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Monthly monitoring"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "Sites break quietly when plugins update or new pages go live. We monitor crawl errors, indexing and Core Web Vitals every month and fix new issues before they cost you rankings."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Website migration SEO for law firms"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "Moving to a new domain, platform or design? Migration SEO, including full redirect mapping and checks after launch, is a separate project quoted on its own."
                }{" "}
                <Link
                  to="/services/law-firm-website-development"
                  className="text-gold-deep underline underline-offset-4"
                >
                  website development for law firms
                </Link>
              </p>
            </article>
          </div>
        </Section>
      </div>
      <div className="bg-background">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"How Our Technical SEO Audit for Law Firms Works"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Every technical SEO audit for attorneys follows the path a page takes through Google: discovered, crawled, rendered, indexed and ranked. We check each stage and fix the first place your pages get stuck."
            }
          </p>
          <figure className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
            <img
              src="/images/services/technical-seo-for-law-firms/crawl-render-index-pipeline.svg"
              alt={
                "Five stages a law firm web page passes through in Google, discovered, crawled, rendered, indexed and ranked, with the common problem that stops pages at each stage."
              }
              width="1200"
              height="500"
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
            <figcaption className="border-t border-border px-5 py-3 text-sm text-muted-foreground">
              {
                "A page can only rank once it has cleared every earlier stage, so we fix problems in that order."
              }
            </figcaption>
          </figure>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Crawl the site"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "We run a full crawl and compare it with your Google Search Console data, so we see your site the way Google does."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Check what Google indexes"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "Search Console's Page indexing report shows which pages are left out and why. We go through every reason that affects a page you care about."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Measure speed and Core Web Vitals"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "PageSpeed Insights and the Core Web Vitals report show how fast your pages are for real visitors on mobile and desktop."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Prioritise the fixes"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "You get a fix list ranked by impact on the pages that bring in cases, not a 200 line export of every warning a tool can produce."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Fix or hand over"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "Depending on what suits your firm, we make the fixes on your site ourselves, give your developer clear instructions for each one, or split the work."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Verify and monitor"}</h3>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {
                  "We re-crawl, request indexing where it's needed and confirm each fix in Search Console, then keep monitoring every month."
                }
              </p>
            </article>
          </div>
          <p className="mt-8 text-sm leading-relaxed text-muted-foreground">
            Tools we use:{" "}
            <a
              href="https://search.google.com/search-console/about"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold-deep underline underline-offset-4"
            >
              Google Search Console
            </a>
            {", "}
            <a
              href="https://pagespeed.web.dev/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold-deep underline underline-offset-4"
            >
              PageSpeed Insights
            </a>
            {", "}
            <a
              href="https://search.google.com/test/rich-results"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold-deep underline underline-offset-4"
            >
              Rich Results Test
            </a>
            {", "}
            <a
              href="https://www.screamingfrog.co.uk/seo-spider/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold-deep underline underline-offset-4"
            >
              Screaming Frog SEO Spider
            </a>
            {", "}
            <a
              href="https://ahrefs.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold-deep underline underline-offset-4"
            >
              Ahrefs
            </a>
            {" and "}
            <a
              href="https://www.semrush.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold-deep underline underline-offset-4"
            >
              Semrush
            </a>
            , plus manual checks of your key pages.
          </p>
        </Section>
      </div>
      <div className="border-y border-border bg-secondary/40">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"Core Web Vitals for Law Firm Websites"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Core Web Vitals are Google's measures of real visitor experience: how quickly the main content appears (LCP), how quickly the page responds when someone taps or types (INP) and how much the layout jumps while it loads (CLS). INP replaced First Input Delay in March 2024, so an older audit may be measuring the wrong thing. Page speed optimization for attorneys matters most on mobile, where someone searching after an accident or an arrest is often on a weak connection and will go straight back to the results if your page stalls."
            }
          </p>
          <figure className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
            <img
              src="/images/services/technical-seo-for-law-firms/core-web-vitals-thresholds.svg"
              alt={
                "Google's Core Web Vitals thresholds: LCP good at 2.5 seconds or less and poor above 4 seconds, INP good at 200 milliseconds or less and poor above 500, CLS good at 0.1 or less and poor above 0.25."
              }
              width="1200"
              height="560"
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
            <figcaption className="border-t border-border px-5 py-3 text-sm text-muted-foreground">
              {
                "A page passes when 75 percent of real visits fall in the good range for all three measures."
              }
            </figcaption>
          </figure>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Speed won't outrank a more relevant page on its own. Google is clear that it still shows the most relevant content even when page experience is weaker, so we fix speed alongside crawling, indexing and content, never as a standalone project."
            }
          </p>
        </Section>
      </div>
      <div className="bg-background">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"Schema Markup for Law Firms and Attorneys"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Schema markup tells search engines in structured data what your firm is, who your attorneys are and which services you offer. Good schema markup for attorneys uses LegalService for the firm and each office, Person for each lawyer and Service for each practice page, all connected to each other. Many law firm sites still use the older Attorney type, which schema.org has deprecated in favour of LegalService."
            }
          </p>
          <figure className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
            <img
              src="/images/services/technical-seo-for-law-firms/law-firm-schema-graph.svg"
              alt={
                "Diagram of connected schema markup for a fictional law firm: a LegalService entity linked to an attorney Person, a practice area Service page and a BreadcrumbList."
              }
              width="1200"
              height="580"
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
            <figcaption className="border-t border-border px-5 py-3 text-sm text-muted-foreground">
              {
                "Each type of markup points to the others, so search engines see one connected firm rather than separate pages."
              }
            </figcaption>
          </figure>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Schema isn't a direct ranking factor, and FAQ rich results are now limited to authoritative government and health sites, so we don't sell markup as a shortcut to stars in search results. Technical SEO and schema markup for attorneys should do one job well: make your firm, your lawyers and your services easy for search engines and AI answers to understand."
            }
          </p>
        </Section>
      </div>
      <div className="border-y border-border bg-secondary/40">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"Check Your Core Web Vitals"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Run your homepage or a key practice page through PageSpeed Insights and enter the three mobile numbers it shows. You'll see how each one compares with Google's thresholds and what usually causes the problem on law firm sites."
            }
          </p>
          <CoreWebVitalsChecker />
        </Section>
      </div>
      <section id="free-plan" className="scroll-mt-24 bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-20">
          <HeroLeadForm
            layout="split"
            headingLevel="h2"
            eyebrow={"FREE TECHNICAL SEO AUDIT"}
            heading={"Get a free technical review of your firm's website."}
            intro={
              "Send us your website and we'll come back within 24 hours with the technical issues holding your pages back and what to fix first."
            }
            sideContent={
              <>
                <p className="mt-7 text-sm font-bold">{"What we'll look at for your firm:"}</p>
                <ul className="mt-4 space-y-4 text-sm leading-relaxed text-primary-foreground/80">
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">{"Pages Google can't crawl or hasn't indexed"}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"Mobile speed and Core Web Vitals on your key pages"}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"Missing or broken schema for your firm and attorneys"}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"Broken links, redirect chains and duplicate pages"}
                    </span>
                  </li>
                </ul>
                <p className="mt-6 text-xs text-primary-foreground/60">
                  Prefer to talk first?{" "}
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold underline underline-offset-4"
                  >
                    Book a strategy call
                  </a>
                </p>
              </>
            }
          />
        </div>
      </section>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"Technical SEO for Lawyers in Every Practice Area"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Technical problems follow the way each practice builds its site. Personal injury firms often run many city and case-type pages that end up competing with each other. Immigration firms serving clients in more than one language need language versions set up so Google shows the right one. Technical SEO for divorce attorneys often starts with merging thin, overlapping pages on custody, support and property. We start with the issues your practice is most likely to have."
          }
        </p>
        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "personal-injury-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"technical seo for personal injury law firms"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "immigration-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"technical seo for immigration law firms"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "criminal-defense-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"technical seo for criminal defense law firms"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "family-law-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"technical seo for family law attorneys"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "estate-planning-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"technical seo for estate planning attorneys"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas"
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"all practice areas"}
            </Link>
          </li>
        </ul>
      </Section>
      <PricingTeaser
        title={"Technical SEO Services for Attorneys, Included in Every SEO Plan"}
        sub={
          "You don't pay extra for technical SEO. Audits, fixes and monthly monitoring are part of every SEO plan, which keeps these affordable technical SEO services for law firms within reach of solo attorneys as well as multi-office firms. Website migrations are quoted separately."
        }
      />
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"Why Firms Choose PageOne as Their Technical SEO Agency for Law Firms"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "We're a technical SEO company for law firms only, so we know the platforms, plugins, chat widgets and intake tools legal sites run on, and the problems they cause. Your technical SEO consultant for law firms explains each issue in plain language and ranks fixes by their effect on your cases, not by how red a tool report looks. If you already have a developer or web agency, your technical SEO consultant for attorneys works alongside them. If you'd rather not deal with it, our technical SEO experts for law firms make the fixes themselves."
          }
        </p>
      </Section>
      <CaseStudy {...serviceCaseStudy("Technical SEO for Law Firms")} semanticSubheadings />
      <TrustSignals
        semanticSubheadings
        intro="Every technical seo for law firms engagement is delivered by the same senior team, on the same guarantees, with reporting tied to signed cases — not clicks."
      />
      <FAQSection
        tone="muted"
        title={"Lawyer Technical SEO: Questions Firms Ask"}
        items={FAQS}
        semanticQuestionHeadings
      />
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-5xl px-5 py-14 text-center sm:px-6 sm:py-20">
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"Ready for a Website Google Can Read?"}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-primary-foreground/80">
            {"Get a free technical review of your website, with a clear list of what to fix first."}
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
            <CTAButton to="#free-plan" variant="gold">
              Get my free technical audit
            </CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">
              Book a strategy call
            </CTAButton>
          </div>
        </div>
      </section>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">{"Related Services"}</h2>
        <ul className="mt-6 flex flex-wrap gap-2">
          {services
            .filter((item) => item.slug !== "technical-seo-for-law-firms")
            .map((item) => (
              <li key={item.slug}>
                <Link
                  to="/services/$slug"
                  params={{ slug: item.slug }}
                  className="inline-block rounded-full border border-border px-4 py-2 text-sm transition hover:border-gold hover:text-gold-deep"
                >
                  {item.name}
                </Link>
              </li>
            ))}
        </ul>
      </Section>
    </Layout>
  );
}
